import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { calculateFee, getTotalAmount } from '../../utils/communityHelpers';
import config from '../../config';

const stripePromise = loadStripe('pk_test_51S9cgzDJrqPK4pxyfv9O2belxOZtNU0z9Da0subpw6gALUddGKOv4rdOlT3EiKuJ43nfjsCkrdHHIrmxhDA1d1bl00kykw0bFP');

/**
 * DonateModal Component
 * Multi-step donation modal with product types, amounts, and Stripe integration
 */
const DonateModalContent = ({ community, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  
  const [step, setStep] = useState(1); // 1 = selection, 2 = confirmation
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [dedicationMessage, setDedicationMessage] = useState('');
  const [coverFee, setCoverFee] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmationNumber, setConfirmationNumber] = useState(null);
  
  const productTypes = [
    { id: 'general', name: 'General', emoji: '🕍' },
    { id: 'torah_fund', name: 'Torah Fund', emoji: '📚' },
    { id: 'education', name: 'Education', emoji: '🎓' },
  ];
  
  const suggestedAmounts = [18, 72, 120, 180];
  
  const getInitials = (name) => {
    if (!name) return 'C';
    const words = name.split(' ');
    if (words.length >= 2) {
      return words[0][0] + words[1][0];
    }
    return name.substring(0, 2);
  };
  
  const getDonationAmount = () => {
    if (customAmount) {
      return parseFloat(customAmount);
    }
    return selectedAmount || 0;
  };
  
  const fee = coverFee ? calculateFee(getDonationAmount()) : 0;
  const totalAmount = getTotalAmount(getDonationAmount(), coverFee);
  
  const handleContinue = async () => {
    if (!getDonationAmount() || getDonationAmount() <= 0) {
      setError('Please select or enter an amount');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Create payment intent
      const response = await fetch(`${config.API_URL}/donations/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          group_id: community.id,
          amount: getDonationAmount(),
          product_type: selectedProductType,
          dedication_message: dedicationMessage,
          cover_fee: coverFee,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create payment');
      }
      
      // Confirm payment with Stripe
      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        data.clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );
      
      if (stripeError) {
        throw new Error(stripeError.message);
      }
      
      if (paymentIntent.status === 'succeeded') {
        setConfirmationNumber(data.confirmationNumber);
        setStep(2);
      }
    } catch (err) {
      console.error('Donation error:', err);
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const renderStep1 = () => (
    <>
      {/* Header */}
      <div style={{ position: 'relative', padding: '20px', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>
        <div style={{ width: '40px', height: '4px', background: '#D1D1D6', borderRadius: '2px', margin: '0 auto 16px' }} />
        
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            width: '40px',
            height: '40px',
            border: 'none',
            background: 'none',
            fontSize: '24px',
            color: '#666',
            cursor: 'pointer'
          }}
        >
          ✕
        </button>
        
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
          {community?.logo_url ? (
            <img src={community.logo_url} alt={community.name} style={{ width: '60px', height: '60px', borderRadius: '30px' }} />
          ) : (
            <div style={{ width: '60px', height: '60px', borderRadius: '30px', background: '#667eea', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '20px', fontWeight: 700 }}>
              {getInitials(community?.name)}
            </div>
          )}
          <div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#000' }}>Make a Donation</div>
            <div style={{ fontSize: '14px', color: '#666' }}>{community?.name}</div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div style={{ padding: '24px', maxHeight: '60vh', overflowY: 'auto' }}>
        {/* Product Types */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>What would you like to support?</h3>
          <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px' }}>
            {productTypes.map(type => (
              <div
                key={type.id}
                onClick={() => setSelectedProductType(type.id)}
                style={{
                  minWidth: '120px',
                  height: '100px',
                  borderRadius: '12px',
                  border: selectedProductType === type.id ? '2px solid #6366F1' : '2px solid #E0E0E0',
                  background: selectedProductType === type.id ? 'rgba(99,102,241,0.05)' : '#fff',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>{type.emoji}</div>
                <div style={{ fontSize: '14px', fontWeight: 600, textAlign: 'center' }}>{type.name}</div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Suggested Amounts */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>Select Amount</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
            {suggestedAmounts.map(amount => (
              <button
                key={amount}
                onClick={() => {
                  setSelectedAmount(amount);
                  setCustomAmount('');
                }}
                style={{
                  padding: '16px',
                  borderRadius: '8px',
                  border: selectedAmount === amount && !customAmount ? 'none' : '1px solid #E0E0E0',
                  background: selectedAmount === amount && !customAmount ? '#6366F1' : '#fff',
                  color: selectedAmount === amount && !customAmount ? '#fff' : '#000',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                ${amount}
              </button>
            ))}
          </div>
        </div>
        
        {/* Custom Amount */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ fontSize: '14px', color: '#666', marginBottom: '8px', display: 'block' }}>Or enter custom amount</label>
          <input
            type="number"
            placeholder="$"
            value={customAmount}
            onChange={(e) => {
              setCustomAmount(e.target.value);
              setSelectedAmount(null);
            }}
            style={{
              width: '100%',
              padding: '16px',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              fontSize: '24px',
              fontWeight: 600
            }}
          />
        </div>
        
        {/* Dedication Message */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ fontSize: '14px', color: '#666', marginBottom: '8px', display: 'block' }}>Dedication Message (Optional)</label>
          <textarea
            placeholder="Add a dedication message (optional)"
            value={dedicationMessage}
            onChange={(e) => setDedicationMessage(e.target.value)}
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '12px',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </div>
        
        {/* Cover Fee */}
        {getDonationAmount() > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <div
              onClick={() => setCoverFee(!coverFee)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px',
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                background: '#F9F9F9',
                cursor: 'pointer'
              }}
            >
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600 }}>Cover platform fee (${fee.toFixed(2)})</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Help us cover processing costs</div>
              </div>
              <input type="checkbox" checked={coverFee} onChange={() => setCoverFee(!coverFee)} />
            </div>
          </div>
        )}
        
        {/* Summary */}
        {getDonationAmount() > 0 && (
          <div style={{ marginBottom: '24px', padding: '16px', background: '#f9f9f9', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span>Donation</span>
              <span>${getDonationAmount().toFixed(2)}</span>
            </div>
            {coverFee && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>Platform fee</span>
                <span>${fee.toFixed(2)}</span>
              </div>
            )}
            <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '8px', marginTop: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                <span>Total</span>
                <span>${totalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}
        
        {/* Card Element */}
        <div style={{ marginBottom: '24px', padding: '16px', border: '1px solid #E0E0E0', borderRadius: '8px' }}>
          <CardElement />
        </div>
        
        {error && (
          <div style={{ padding: '12px', background: '#FEE', borderRadius: '8px', color: '#C00', marginBottom: '16px' }}>
            {error}
          </div>
        )}
      </div>
      
      {/* Bottom Action */}
      <div style={{ padding: '20px', borderTop: '1px solid #e0e0e0' }}>
        <button
          onClick={handleContinue}
          disabled={!getDonationAmount() || getDonationAmount() <= 0 || loading}
          style={{
            width: '100%',
            height: '56px',
            background: getDonationAmount() > 0 ? '#6366F1' : '#ccc',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            fontSize: '18px',
            fontWeight: 600,
            cursor: getDonationAmount() > 0 ? 'pointer' : 'not-allowed',
            boxShadow: getDonationAmount() > 0 ? '0 4px 12px rgba(99,102,241,0.3)' : 'none'
          }}
        >
          {loading ? 'Processing...' : 'Continue to Payment'}
        </button>
      </div>
    </>
  );
  
  const renderStep2 = () => (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '8px' }}>Thank You!</h2>
      <p style={{ fontSize: '16px', color: '#666', marginBottom: '24px' }}>{community?.name}</p>
      
      <div style={{ padding: '16px', background: '#4CAF50', borderRadius: '8px', margin: '24px 0', color: '#fff' }}>
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>✓</div>
        <div style={{ fontSize: '16px', fontWeight: 700 }}>Payment successful</div>
        <div style={{ fontSize: '14px', marginTop: '8px' }}>
          Your confirmation number is<br />
          <strong>{confirmationNumber}</strong>
        </div>
      </div>
      
      <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '16px', marginTop: '16px' }}>
        <div style={{ marginBottom: '8px' }}>Total donated: <strong>${totalAmount.toFixed(2)}</strong></div>
        <div style={{ color: '#666', fontSize: '14px' }}>
          {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </div>
      </div>
      
      <button
        onClick={onClose}
        style={{
          marginTop: '24px',
          width: '100%',
          height: '48px',
          background: '#6366F1',
          color: '#fff',
          border: 'none',
          borderRadius: '12px',
          fontSize: '16px',
          fontWeight: 600,
          cursor: 'pointer'
        }}
      >
        Done
      </button>
    </div>
  );
  
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          borderRadius: '16px 16px 0 0',
          overflow: 'hidden'
        }}
      >
        {step === 1 ? renderStep1() : renderStep2()}
      </div>
    </div>
  );
};

const DonateModal = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <DonateModalContent {...props} />
    </Elements>
  );
};

export default DonateModal;
